import type { FoundationResponse } from '@@/api/learnings/foundation/foundation-handler';
import type { LearningsStatus } from '@@/api/learnings/learnings-status';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from 'hooks/useAuth';
import { useClient } from 'hooks/useClient';
import { can } from 'definitions/capabilities';
import { apiErrorMessage } from '../api-error';
import { useBillingState } from '../billing/useBillingState';
import { useWebsites } from '../useWebsites';
import { roundEstimate } from './useLearningsBackfill';

export type FoundationFilter = 'need' | 'ready' | 'all';

/** Rows per page of the foundation check, as the design shows it. */
export const FOUNDATION_PAGE_SIZE = 5;

/**
 * The setup screen: what each concluded test has written down, what turning learnings on would
 * write and cost, and the switch itself.
 *
 * "Write learnings for N tests" saves the website's learnings settings (on, screenshots, data
 * sharing) and then writes the past tests: the switch alone covers only tests that end from now
 * on (useLearningsBackfill). When the foundation check found gaps it asks first; the gaps never
 * block it (decided 2026-10-20), they only make the learnings vaguer.
 */
export const useLearningsSetup = () => {
  const client = useClient();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { website, accountOptedOut } = useWebsites();
  const websiteId: number = website?.id ?? 0;
  const { billingState } = useBillingState();

  const status = useQuery<LearningsStatus>({
    queryKey: ['LEARNINGS_STATUS', websiteId],
    enabled: !!websiteId,
    queryFn: () => client.get<LearningsStatus>(`/learnings/${websiteId}/status`),
  });
  const foundation = useQuery<FoundationResponse>({
    queryKey: ['LEARNINGS_FOUNDATION', websiteId],
    enabled: !!websiteId,
    queryFn: () => client.get<FoundationResponse>(`/learnings/${websiteId}/foundation`),
  });

  const [filter, setFilter] = useState<FoundationFilter>('need');
  const [page, setPage] = useState(1);
  const [readScreenshots, setReadScreenshots] = useState<boolean>(website?.learningsReadScreenshots ?? true);
  // The account's opt-out wins over the website's own switch, and only the owning account may change it.
  const mayChangeSharing = !accountOptedOut && user?.companyId != null && user.companyId === website?.companyId;
  const [shareData, setShareData] = useState<boolean>(
    !accountOptedOut && !(website?.learningsContributionOptOut ?? false),
  );
  const [confirming, setConfirming] = useState(false);

  const tests = foundation.data?.tests ?? [];
  const counts = foundation.data?.counts ?? null;
  const filtered = tests.filter((test) => {
    if (filter === 'all') return true;
    const ready = !test.note;
    return filter === 'ready' ? ready : !ready;
  });
  const pageCount = Math.max(1, Math.ceil(filtered.length / FOUNDATION_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);

  const past = status.data?.past ?? 0;
  const creditsPerCard = status.data?.creditsPerCard ?? 0;

  const start = useMutation({
    mutationFn: async () => {
      await client.put(`/websites/${websiteId}/settings`, {
        learningsEnabled: true,
        learningsReadScreenshots: readScreenshots,
        // Sent only by the owning account: anyone else would be refused for a switch they did not touch.
        ...(mayChangeSharing ? { learningsContributionOptOut: !shareData } : {}),
      });
      if (past > 0) await client.post(`/learnings/${websiteId}/backfill`, {});
    },
    onSuccess: () => {
      setConfirming(false);
      void queryClient.invalidateQueries({ queryKey: ['CLIENT_ACCOUNTS'] });
      void queryClient.invalidateQueries({ queryKey: ['WEBSITES'] });
      void queryClient.invalidateQueries({ queryKey: ['LEARNINGS_STATUS', websiteId] });
    },
    onError: (error) => toast.error(apiErrorMessage(error, 'Could not turn learnings on.')),
  });

  const needWork = counts?.needWork ?? 0;

  return {
    website,
    canManage: can(user, 'learnings:manage'),
    isLoading: status.isLoading || foundation.isLoading,
    concluded: counts?.total ?? 0,
    counts,
    filter,
    setFilter: (value: FoundationFilter) => {
      setFilter(value);
      setPage(1);
    },
    rows: filtered.slice((currentPage - 1) * FOUNDATION_PAGE_SIZE, currentPage * FOUNDATION_PAGE_SIZE),
    filteredCount: filtered.length,
    currentPage,
    pageCount,
    setPage,
    past,
    estimate: roundEstimate(past * creditsPerCard),
    perTest: Math.round(creditsPerCard),
    /** Null when the account is not on AI credits (invoice, free): its monthly cap applies instead. */
    creditsLeft: billingState?.aiCredits ? Math.floor(billingState.aiCredits.balance) : null,
    readScreenshots,
    setReadScreenshots,
    shareData,
    setShareData,
    mayChangeSharing,
    accountOptedOut,
    needWork,
    /** The CTA: asks first when tests have gaps, writes at once otherwise. */
    request: () => (needWork > 0 ? setConfirming(true) : start.mutate()),
    confirming,
    cancel: () => setConfirming(false),
    confirm: () => start.mutateAsync().catch(() => undefined),
    isStarting: start.isPending,
  };
};

export type LearningsSetupManager = ReturnType<typeof useLearningsSetup>;
